import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BannerImage from "../../assets/images/store-banner.png";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import products from "../../mocks/product.json";
import styles from "./index.module.css";
import { useNavigate, useParams } from "react-router-dom";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import Nav from "../../components/Nav";
import Breadcrumb from "../../components/Breadcrumb";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SubcategoryList from "../Products/subCategoryList";

const flattenProducts = products.map((p) => p.list).flat();

const pageSize = 5;

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

function Product(props) {
  const navigate = useNavigate();
  const params = useParams();
  const { categoryId, productId, subId} = useParams();
  const [curTab, setCurTab] = useState(0);
  const [page, setPage] = useState(1);
  const [filterIds, setFilterIds] = useState([]);
  const category = products.find((c) => c.id === categoryId);
  const product = category?.list.find((p) => p.id.toString() === productId);
  const subcategory = product?.subcategories?.find(
    (s) => s.id.toString() === subId
  );
  const filteredProducts = useMemo(() => {
    if (filterIds.length) {
      return flattenProducts.filter((p) => filterIds.includes(p.id));
    }
    return flattenProducts;
  }, [filterIds]);

  const paginatedProducts = useMemo(() => {
    const acpage = page - 1;
    const offset = acpage * pageSize;
    return filteredProducts.slice(offset, offset + pageSize);
  }, [page, filteredProducts]);

  const onFilter = (id) => {
    const isAdded = filterIds.includes(id);
    let newIds = [];
    if (isAdded) {
      newIds = filterIds.filter((_id) => _id !== id);
    } else {
      newIds = [...filterIds, id];
    }
    setPage(1);
    setFilterIds(newIds);
  };
  if (!category || !product) {
    return <div>Product Not Found</div>;
  }
  if (product.subcategories && product.subcategories.length > 0 && !subId) {
    return <SubcategoryList />;
  }
  const itemToShow = subId && subcategory ? subcategory : product;

  const getBackUrl = (categoryId, productId, subId) => {
    if (subId) {
      return `/medicalsupplies/${categoryId}/${productId}`;
    } else if (productId) {
      return `/medicalsupplies/${categoryId}`;
    } else {
      return `/medicalsupplies`;
    }
  };

  return (
    <Stack className={styles.landingPage} component="main" direction="column">
      <Stack className={styles.mainSection}>
        <Box className={styles.heroBanner}>
          <img src={BannerImage} alt="hero-banner" />
        </Box>
        <Box className={styles.content}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={{
                height: "32px",
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  backgroundColor: "white",
                  borderRadius: 0,
                },
                "& .MuiInputBase-root": {
                  paddingRight: "2px",
                },
                "& .MuiInputBase-input": {
                  paddingRight: "32px",
                },
              }}
              placeholder="Search"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack color="primary" sx={{}}>
                      <SearchIcon
                        sx={{
                          backgroundColor: "#0a9a73",
                          color: "white",
                          padding: "2px",
                        }}
                      />
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  background: "white",
                  height: "30px",
                  borderRadius: "4px",
                  padding: "8px",
                  boxSizing: "border-box",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: "center" }}
                >
                  <Typography sx={{ lineHeight: 1 }} variant="overline">
                    EN
                  </Typography>
                  <ExpandMoreIcon sx={{ stroke: 0.5 }} />
                </Stack>
                <Divider sx={{ mx: "4px" }} orientation="vertical" />
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ alignItems: "center" }}
                >
                  <Typography sx={{ lineHeight: 1 }} variant="overline">
                    USD
                  </Typography>
                  <ExpandMoreIcon />
                </Stack>
              </Stack>
              <Button
                variant="contained"
                size="small"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Login | Pay Bill
              </Button>
              <Tooltip title="Login | Pay Bill">
                <IconButton
                  variant="contained"
                  size="large"
                  sx={{ color: "white", display: { xs: "flex", sm: "none" } }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Nav />
          <Breadcrumb
            links={[
              { title: "Home", url: "/" },
              { title: "Medical Supplies", url: "/medicalsupplies" },
              { title: category.title, url: `/medicalsupplies/${categoryId}` },
              { title: product.title, url: `/medicalsupplies/${categoryId}/${productId}` },
              ...(subId && subcategory ? [{ title: subcategory.title }] : []),
            ]}
          />
        </Box>
      </Stack>
      <Box sx={{ background: "white" }}>
        <Box className={styles.content}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{ m: "2rem" }}
            spacing={3}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "2px solid #84848429",
              }}
            >
              <Box
                component="img"
                sx={{
                  width: { xs: "80%", sm: "60%", md: "50%" },
                  height: "auto",
                }}
                src={itemToShow.img || product.img}
                alt="product"
              />
            </Box>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Stack direction="row" spacing={10} alignItems="center" justifyContent="space-between">
              <Typography variant="h4">{itemToShow.title}</Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate(getBackUrl(categoryId, productId, subId))}
                  sx={{ mb: 3 }}
                 >
                  Back
                </Button>         
              </Stack>           
              <Typography variant="body1" sx={{ color: "#666E77" }}>
                {itemToShow.description}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "#666E77" }}>
                Please call our Customer Care Team at 1-866-422-4866 or fill out
                the form below to see if you qualify for a CGM sensor with $0
                out-of-pocket costs.
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField placeholder="First Name" size="small" />
                  <TextField
                    placeholder="Last Name"
                    size="small"
                    sx={{
                      flexGrow: 1,
                      "& .MuiOutlinedInput-root": {
                        border: "1px solid #e2e4e8",
                        boxShadow: "0px 5px 8px #84848429", // Custom box shadow
                      },
                    }}
                  />
                </Stack>
                <TextField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      border: "1px solid #e2e4e8",
                      boxShadow: "0px 5px 8px #84848429", // Custom box shadow
                    },
                  }}
                  placeholder="Email Address"
                  size="small"
                />
                <TextField
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      border: "1px solid #e2e4e8",
                      boxShadow: "0px 5px 8px #84848429", // Custom box shadow
                    },
                  }}
                  placeholder="Phone Number"
                  size="small"
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }}>
                <Stack direction="row" sx={{ alignItems: "center" }}>
                  <Checkbox />
                  <Typography variant="body2">I Agree to&nbsp;</Typography>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ textDecoration: "underline" }}
                  >
                    Terms and Conditions
                  </Typography>
                </Stack>

                <Button
                  sx={{
                    marginLeft: { xs: "none", sm: "auto" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    lineheight: "16px",
                  }}
                  variant="contained"
                >
                  Submit
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ background: "#F2F5F7 0% 0% no-repeat padding-box" }}>
          <Box className={styles.content}>
            <Box sx={{ p: "3rem" }}>
              <Tabs
                value={curTab}
                onChange={(_, nextTab) => setCurTab(nextTab)}
                aria-label="basic tabs example"
              >
                <Tab label="Description" value={0} />
                <Tab label="Additional Info" value={1} />
                <Tab label="Reviews" />
                <Tab label="Video" />
              </Tabs>
              <CustomTabPanel
                value={curTab}
                index={0}
                sx={{
                  padding: "2rem 1.5rem",
                  mt: "1rem",
                  background: "white",
                }}
              >
                <Typography variant="h4" sx={{ mb: "1rem" }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ color: "#666E77" }}>
                  Placeholder for content.
                </Typography>
                <Typography variant="h4" sx={{ my: "1rem" }}>
                  More Details
                </Typography>
                <Stack direction="row" spacing={1} sx={{ color: "#666E77" }}>
                  <TrendingFlatIcon />
                  <Typography variant="body1">
                    Placeholder for content.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ color: "#666E77" }}>
                  <TrendingFlatIcon />
                  <Typography variant="body1">
                    Placeholder for content.
                  </Typography>
                </Stack>
              </CustomTabPanel>
              <CustomTabPanel
                value={curTab}
                index={1}
                sx={{
                  padding: "2rem 1.5rem",
                  mt: "1rem",
                  background: "white",
                }}
              >
                <Typography variant="h4" sx={{ mb: "1rem" }}>
                  Additional Information
                </Typography>
                <Typography variant="body1" sx={{ color: "#666E77" }}>
                  Placeholder for content.
                </Typography>
              </CustomTabPanel>
              <CustomTabPanel
                value={curTab}
                index={2}
                sx={{
                  padding: "2rem 1.5rem",
                  mt: "1rem",
                  background: "white",
                }}
              >
                <Typography variant="h4" sx={{ mb: "1rem" }}>
                  Reviews
                </Typography>
                <Typography variant="body1" sx={{ color: "#666E77" }}>
                  Placeholder for content.
                </Typography>
              </CustomTabPanel>
              <CustomTabPanel
                value={curTab}
                index={3}
                sx={{ padding: "2rem 1.5rem", mt: "1rem", background: "white" }}
              >
                <Typography variant="h4" sx={{ mb: "1rem" }}>
                  Video
                </Typography>
                <Typography variant="body1" sx={{ color: "#666E77" }}>
                  Placeholder for content.
                </Typography>
              </CustomTabPanel>
            </Box>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}

Product.propTypes = {};

export default Product;
